import { ListChecks, X } from 'lucide-react';
import { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';
import type { Compartment, Planogram as PlanogramData } from '@/lib/planogram-api';

type Kolumna = PlanogramData['drawers'][number];

/**
 * PASEK ZAZNACZENIA — WYŁĄCZNIE zaznaczanie. Nic tu nie zmienia danych.
 *
 * ⚠️ TO JEST ROZSTRZYGNIĘCIE, NIE UKŁAD GRAFICZNY, i było wprost zamówione: w starym Vendi
 * zmiany robiło się „raz tu, raz tam" i użytkownik zgłosił to jako niekonsekwencję. U nas
 * wszystko, co MODYFIKUJE, siedzi w panelu bocznym (`PanelEdycji`), a ten pasek liczy zaznaczone
 * przegródki i pozwala je rozszerzyć albo wyczyścić. Dokładanie tu pola edycji łamie tę zasadę.
 *
 * ⚠️ POKAZUJE SIĘ DOPIERO PRZY WIĘCEJ NIŻ JEDNEJ PRZEGRÓDCE. Przy jednej cały opis mieści się
 * w panelu bocznym, a pasek byłby drugim miejscem mówiącym „zaznaczono: 1" i zabierałby ostatni
 * wiersz siatki.
 *
 * ⚠️ ODSUNIĘTY OD PRAWEJ O SZEROKOŚĆ PANELU. Panel jest `fixed` i zawsze otwarty, gdy coś jest
 * zaznaczone — pasek na pełną szerokość wchodziłby pod niego.
 */
export function PasekZaznaczenia({
  zaznaczone,
  gdzieJest,
  onZaznaczKolumny,
  onWyczysc,
}: {
  zaznaczone: ReadonlySet<string>;
  gdzieJest: Map<string, { kolumna: Kolumna; c: Compartment }>;
  /** Rozszerza zaznaczenie do PEŁNYCH kolumn, których dotyka — najczęstszy ruch po shifcie. */
  onZaznaczKolumny: () => void;
  onWyczysc: () => void;
}) {
  const { t } = useI18n();

  const opis = useMemo(() => {
    const trafienia = [...zaznaczone].map((id) => gdzieJest.get(id)).filter((x) => x !== undefined);
    const zTowarem = trafienia.filter((x) => x.c.item !== null).length;
    const kolumny = [...new Set(trafienia.map((x) => x.kolumna.drawerNo))].sort((a, b) => a - b);
    const pelne = trafienia.every((x) => x.kolumna.compartments.every((c) => zaznaczone.has(c.id)));
    return { ile: trafienia.length, zTowarem, puste: trafienia.length - zTowarem, kolumny, pelne };
  }, [zaznaczone, gdzieJest]);

  return (
    /*
      ⚠️ PŁYWAJĄCY KAFELEK, NIE LISTWA NA CAŁĄ SZEROKOŚĆ. Pasek na pełną szerokość w kolorze
      karty zlewał się z tłem strony i zgłoszono, że „ledwo widać". Zwężony do własnej
      zawartości, wyśrodkowany i w kolorze akcentu odcina się od siatki, a przy okazji nie
      przykrywa lewej krawędzi z numerami wierszy.

      ⚠️ WYŚRODKOWANY W POLU BEZ PANELU: `lg:right-[23rem]` przesuwa prawą krawędź obszaru
      pozycjonowania, więc `mx-auto` środkuje kafelek względem TEGO, co widać, a nie względem
      całego okna — inaczej kafelek uciekałby w lewo spod panelu.
    */
    <div className="pointer-events-none fixed bottom-4 left-0 right-0 z-30 flex justify-center px-4 lg:right-[min(23rem,92vw)]">
      {/*
        ⚠️ DWIE WARSTWY TŁA, BO JEDNA MUSI BYĆ NIEPRZEZROCZYSTA. Sam `bg-primary/10` wyglądał
        dobrze nad pustym tłem, ale kafelek pływa NAD SIATKĄ — a nad zieloną komórką przebijał
        kolor przegródki i napisu nie dało się przeczytać (zgłoszone). Zewnętrzna warstwa jest
        krytą kartą, wewnętrzna dokłada odcień akcentu; łącznie kryją, a wygląd zostaje.
      */}
      <div className="pointer-events-auto max-w-full rounded-full bg-card shadow-lg">
        <div className="flex max-w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-full border border-primary/40 bg-primary/10 px-4 py-2 text-xs">
          <span className="font-semibold text-primary">
            {t('planogram.selectedCount').replace('{n}', String(opis.ile))}
          </span>
          <span className="text-muted-foreground">
            {t('planogram.selectedBreakdown')
              .replace('{withItem}', String(opis.zTowarem))
              .replace('{empty}', String(opis.puste))}
          </span>
          <span className="truncate text-muted-foreground">
            {t('planogram.columnsShort')} {opis.kolumny.join(', ')}
          </span>
          {!opis.pelne && (
            <Button variant="outline" size="sm" className="h-7 bg-card" onClick={onZaznaczKolumny}>
              <ListChecks />
              {t('planogram.selectWholeColumns')}
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-7 bg-card" onClick={onWyczysc}>
            <X />
            {t('planogram.deselect')}
          </Button>
        </div>
      </div>
    </div>
  );
}
